<template>
  <div class="space-y-6">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold text-neutral-800">Administrar instituciones</h1>
        <p class="text-sm text-neutral-500">
          Crea nuevas instituciones y selecciona cuál administrar.
        </p>
      </div>
      <button
        class="px-4 py-2 rounded-md text-white bg-blue-600 hover:bg-blue-700"
        @click="showCreateForm = true"
      >
        Crear institución
      </button>
    </div>

    <div v-if="showCreateForm" class="bg-white rounded-xl border border-neutral-200 p-4">
      <h2 class="text-base font-semibold text-neutral-700 mb-4">Nueva institución</h2>
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-neutral-600 mb-2">Nombre de la institución</label>
          <input
            v-model.trim="createForm.name"
            type="text"
            class="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-700"
            placeholder="Ej: Colegio San Martín"
          />
          <p v-if="createError" class="mt-1 text-sm text-red-600">{{ createError }}</p>
        </div>
        <div>
          <label class="block text-sm text-neutral-600 mb-2">Código (opcional)</label>
          <input
            v-model.trim="createForm.code"
            type="text"
            class="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm text-neutral-700"
            placeholder="Ej: CSM-01"
          />
        </div>
      </div>
      <div class="flex justify-end gap-2 mt-4">
        <button
          type="button"
          class="px-4 py-2 rounded-md text-neutral-600 border border-neutral-300"
          @click="cancelCreate"
        >
          Cancelar
        </button>
        <button
          type="button"
          class="px-4 py-2 rounded-md text-white bg-blue-600 disabled:opacity-50"
          :disabled="createForm.name.length === 0 || isCreating"
          @click="submitCreate"
        >
          {{ isCreating ? "Creando…" : "Crear institución" }}
        </button>
      </div>
    </div>

    <div class="bg-white rounded-xl border border-neutral-200 overflow-hidden">
      <div class="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
        <span class="text-sm font-semibold text-neutral-700">Mis instituciones</span>
        <button
          class="text-sm text-blue-600 hover:underline"
          @click="loadInstitutions(true)"
        >
          Actualizar
        </button>
      </div>
      <div v-if="isLoading" class="px-4 py-6 text-sm text-neutral-500">
        Cargando instituciones...
      </div>
      <div v-else-if="institutions.length === 0" class="px-4 py-6 text-sm text-neutral-500">
        No tienes instituciones registradas.
      </div>
      <div v-else class="divide-y divide-neutral-100">
        <div
          v-for="institution in institutions"
          :key="institution.id"
          class="flex items-center justify-between px-4 py-3"
        >
          <div>
            <p class="text-sm font-semibold text-neutral-800">{{ institution.name }}</p>
            <div class="flex items-center gap-2">
              <p v-if="institution.code" class="text-xs text-neutral-500">{{ institution.code }}</p>
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="institution.active ? 'bg-green-100 text-green-700' : 'bg-neutral-200 text-neutral-600'"
              >
                {{ institution.active ? "Activa" : "Desactivada" }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button
              v-if="institution.active"
              class="h-8 w-8 flex items-center justify-center rounded-md text-blue-600 hover:bg-blue-50"
              @click="setCurrentInstitution(institution.id)"
              title="Usar esta institución"
              aria-label="Usar esta institución"
            >
              <Icon icon="ph:check-circle" height="18" />
            </button>
            <button
              class="h-8 w-8 flex items-center justify-center rounded-md text-amber-600 hover:bg-amber-50"
              @click="toggleInstitutionStatus(institution)"
              :title="institution.active ? 'Desactivar' : 'Activar'"
              :aria-label="institution.active ? 'Desactivar' : 'Activar'"
            >
              <Icon :icon="institution.active ? 'ph:pause-circle' : 'ph:play-circle'" height="18" />
            </button>
            <button
              class="h-8 w-8 flex items-center justify-center rounded-md text-red-600 hover:bg-red-50"
              @click="removeInstitution(institution)"
              title="Eliminar"
              aria-label="Eliminar"
            >
              <Icon icon="ph:trash" height="18" />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue2";
import {
  getMyInstitutions,
  createMyInstitution,
  updateMyInstitutionStatus,
  deleteMyInstitution,
} from "@/services/institutions.service";

export default {
  name: "TeacherInstitutions",
  components: {
    Icon,
  },
  data() {
    return {
      institutions: [],
      isLoading: false,
      showCreateForm: false,
      isCreating: false,
      createForm: {
        name: "",
        code: "",
      },
      createError: "",
    };
  },
  methods: {
    emitInstitutionsUpdated() {
      this.$root.$emit("teacher-institutions-updated", this.institutions);
    },
    async loadInstitutions(forceRefresh = false) {
      if (!forceRefresh) {
        const stored = JSON.parse(localStorage.getItem("teacher_institutions") || "[]");
        if (Array.isArray(stored) && stored.length > 0) {
          this.institutions = stored;
          this.emitInstitutionsUpdated();
          return;
        }
      }
      this.isLoading = true;
      try {
        const list = await getMyInstitutions();
        this.institutions = Array.isArray(list) ? list : [];
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));
        this.emitInstitutionsUpdated();
      } catch (error) {
        console.error("Error cargando instituciones", error);
        this.institutions = [];
      } finally {
        this.isLoading = false;
      }
    },
    cancelCreate() {
      this.showCreateForm = false;
      this.createForm = { name: "", code: "" };
      this.createError = "";
    },
    async submitCreate() {
      if (!this.createForm.name || !this.createForm.name.trim()) {
        this.createError = "El nombre de la institución es obligatorio.";
        return;
      }
      this.isCreating = true;
      this.createError = "";
      try {
        const created = await createMyInstitution({
          name: this.createForm.name.trim(),
          code: this.createForm.code ? this.createForm.code.trim() : null,
        });
        this.institutions = [...this.institutions, created];
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));
        this.emitInstitutionsUpdated();
        this.cancelCreate();
        this.$toasted.success("Institución creada.", {
          position: "top-center",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error creando institución", error);
        this.createError = "No se pudo crear la institución.";
      } finally {
        this.isCreating = false;
      }
    },
    setCurrentInstitution(institutionId) {
      if (!institutionId) return;
      localStorage.setItem("teacher_current_institution_id", institutionId);
      this.$root.$emit("teacher-institution-change", institutionId);
      this.$toasted.success("Institución seleccionada.", {
        position: "top-center",
        duration: 2000,
      });
    },
    async toggleInstitutionStatus(institution) {
      if (!institution || typeof institution.active === "undefined") return;
      const nextStatus = !institution.active;
      const confirmed = window.confirm(
        nextStatus
          ? `¿Deseas activar la institución ${institution.name}?`
          : `¿Deseas desactivar la institución ${institution.name}?`
      );
      if (!confirmed) return;
      try {
        const updated = await updateMyInstitutionStatus(institution.id, nextStatus);
        this.institutions = this.institutions.map((item) =>
          item.id === institution.id ? updated : item
        );
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));
        await this.loadInstitutions(true);
        this.emitInstitutionsUpdated();
        this.$toasted.success(
          nextStatus ? "Institución activada." : "Institución desactivada.",
          { position: "top-center", duration: 3000 }
        );
      } catch (error) {
        console.error("Error actualizando institución", error);
        this.$toasted.error("No se pudo actualizar la institución.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
    async removeInstitution(institution) {
      const confirmed = window.confirm(
        `¿Seguro que deseas eliminar ${institution.name}? Esta acción eliminará también los grupos y los estudiantes asociados.`
      );
      if (!confirmed) return;
      try {
        await deleteMyInstitution(institution.id);
        this.institutions = this.institutions.filter((item) => item.id !== institution.id);
        localStorage.setItem("teacher_institutions", JSON.stringify(this.institutions));
        this.emitInstitutionsUpdated();
        const storedId = localStorage.getItem("teacher_current_institution_id");
        if (storedId && Number(storedId) === institution.id) {
          localStorage.removeItem("teacher_current_institution_id");
        }
        this.$toasted.success("Institución eliminada.", {
          position: "top-center",
          duration: 3000,
        });
      } catch (error) {
        console.error("Error eliminando institución", error);
        this.$toasted.error("No se pudo eliminar la institución.", {
          position: "top-center",
          duration: 3000,
        });
      }
    },
  },
  mounted() {
    this.loadInstitutions();
  },
};
</script>
